/* CONTEXT:START
Component: SubmissionDialog
Status: build
Dependencies: Material-UI, useApiCall hook, apiService
Next: Wave 2 - implement real Joe's AI evaluation backend service
CONTEXT:END */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Add, Assessment } from '@mui/icons-material';
import { CreateSubmissionRequest } from '../../types';
import { apiService } from '../../services/api';
import { useApiCall } from '../../hooks/useApiCall';
import { RetryWrapper } from '../shared';

interface SubmissionDialogProps {
  open: boolean;
  onClose: () => void;
  month: number;
  year: number;
  onSubmissionCreated: () => void;
}

const SubmissionDialog: React.FC<SubmissionDialogProps> = ({
  open,
  onClose,
  month,
  year,
  onSubmissionCreated,
}) => {
  const [type, setType] = useState<'highlight' | 'lowlight'>('highlight');
  const [content, setContent] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [joesFeedback, setJoesFeedback] = useState<string | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setType('highlight');
      setContent('');
      setValidationError(null);
      setJoesFeedback(null);
      setIsEvaluating(false);
    }
  }, [open]);

  const createSubmissionCall = useCallback(() => {
    const submission: CreateSubmissionRequest = {
      type,
      content: content.trim(),
      month,
      year,
    };
    return apiService.createSubmission(submission);
  }, [type, content, month, year]);

  const { loading, error, execute: submitSubmission } = useApiCall(
    createSubmissionCall,
    {
      onSuccess: () => {
        setContent('');
        setType('highlight');
        setValidationError(null);
        onSubmissionCreated();
        onClose();
      }
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) {
      setValidationError('Content is required');
      return;
    }

    if (content.length > 2000) {
      setValidationError('Content must be 2000 characters or less');
      return;
    }

    setValidationError(null);
    await submitSubmission();
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setContent(value);
    
    if (validationError && value.trim()) {
      setValidationError(null);
    }
  };

  const handleEvaluate = async () => {
    if (!content.trim()) return;
    
    setIsEvaluating(true);
    setJoesFeedback(null);
    
    try {
      // Call the real backend API
      const response = await apiService.evaluateSubmission({
        content: content.trim(),
        type,
        month,
        year
      });

      if (response.error) {
        // Handle API errors
        setValidationError(`Joe encountered an issue: ${response.error.error.message}`);
      } else if (response.data) {
        // Display Joe's feedback
        setJoesFeedback(response.data.feedback);
        setValidationError(null);
      }
    } catch (error) {
      console.error('Evaluation error:', error);
      setValidationError('Joe is temporarily unavailable. Please try again later.');
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleClose = () => {
    if (!loading && !isEvaluating) {
      onClose();
    }
  };

  const isEvaluateDisabled = !content.trim() || content.length > 2000 || loading || isEvaluating;
  const isSubmitDisabled = !content.trim() || content.length > 2000 || loading || isEvaluating;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px',
          maxHeight: '90vh', // Prevent dialog from exceeding viewport height
          display: 'flex',
          flexDirection: 'column',
          transition: 'min-height 0.3s ease-in-out',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          Add New Submission
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {new Date(year, month - 1).toLocaleDateString('en-US', { 
            month: 'long', 
            year: 'numeric' 
          })}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ 
        pt: 2, 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden' // Prevent content from overflowing
      }}>
        <Box component="form" onSubmit={handleSubmit}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={type}
              label="Type"
              onChange={(e) => setType(e.target.value as 'highlight' | 'lowlight')}
              disabled={loading}
            >
              <MenuItem value="highlight">Highlight</MenuItem>
              <MenuItem value="lowlight">Lowlight</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={8}
            label="Content"
            value={content}
            onChange={handleContentChange}
            disabled={loading}
            sx={{ mb: 2 }}
            helperText={`${content.length}/2000 characters`}
            error={content.length > 2000}
            placeholder="Enter your submission content here..."
          />

          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <RetryWrapper
            error={error}
            loading={loading}
            onRetry={submitSubmission}
          >
            <></>
          </RetryWrapper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        pb: 3, 
        pt: 1, 
        flexDirection: 'column', 
        gap: 2,
        flexShrink: 0, // Prevent actions from shrinking
        maxHeight: '50vh', // Limit actions section height
        overflow: 'hidden', // Prevent overflow
        display: 'flex'
      }}>
        {/* Evaluation Section */}
        <Box sx={{ 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0, // Allow shrinking
          overflow: 'hidden'
        }}>
          <Divider sx={{ mb: 2, flexShrink: 0 }}>
            <Typography variant="body2" color="text.secondary">
              Quality Assessment
            </Typography>
          </Divider>
          
          {/* Scrollable content area */}
          <Box sx={{ 
            overflowY: 'auto',
            flexGrow: 1,
            minHeight: 0
          }}>
          
          <Button
            variant="outlined"
            startIcon={isEvaluating ? <CircularProgress size={20} /> : <Assessment />}
            onClick={handleEvaluate}
            disabled={isEvaluateDisabled || isEvaluating}
            fullWidth
            sx={{ mb: 2 }}
          >
            {isEvaluating ? 'Joe is thinking...' : 'Ask Joe for an opinion'}
          </Button>
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            Get Joe's AI-powered feedback to improve your submission quality
          </Typography>

          {/* Joe's Feedback Section */}
          {joesFeedback && (
            <Box sx={{ 
              mt: 3, 
              p: 3, 
              bgcolor: 'background.paper', 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'divider',
              maxHeight: '300px', // Limit the maximum height
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                <Assessment sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary.main">
                  Joe's Feedback
                </Typography>
              </Box>
              <Box sx={{ 
                overflowY: 'auto', 
                flexGrow: 1,
                pr: 1, // Add padding for scrollbar
                position: 'relative',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: '4px',
                  '&:hover': {
                    background: 'rgba(0,0,0,0.5)',
                  }
                },
                // Add subtle shadow to indicate scrollable content
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '8px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), transparent)',
                  pointerEvents: 'none',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '8px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.1), transparent)',
                  pointerEvents: 'none',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                },
                '&:hover::before, &:hover::after': {
                  opacity: 1,
                }
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    whiteSpace: 'pre-line',
                    lineHeight: 1.6,
                    '& strong': {
                      fontWeight: 600,
                      color: 'text.primary'
                    }
                  }}
                >
                  {joesFeedback}
                </Typography>
              </Box>
            </Box>
          )}
          </Box> {/* Close scrollable content area */}
        </Box> {/* Close evaluation section */}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end', flexShrink: 0 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitDisabled}
            startIcon={loading ? <CircularProgress size={20} /> : <Add />}
          >
            {loading ? 'Adding...' : 'Add Submission'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SubmissionDialog;
